import { FC, useRef, useState } from "react";
import { z } from "zod";
import { useForm } from "@mantine/form";
import { useQueries } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useSnackbar, VariantType } from "notistack";

import {
  Button,
  DateInput,
  FormSection,
  Grid,
  SelectBox,
  Switch,
  TextAreaInput,
  TextInput,
  useTheme,
} from "@monolith-forensics/monolith-ui";

import { StorageAPI } from "../storage.api";
import LocationsAPI from "@/api/locations";
import {
  CUSTOM_FIELD_CATEGORIES,
  CustomField,
  CustomFieldsQuery,
} from "@/Features/CustomFields";
import ButtonMenu from "@/components/Forms/components/ButtonMenu";
import Loader from "@/components/Loader";

import CustomFieldRender from "@/components/Forms/components/CustomFieldRender";
import resolveCustomFieldValidations from "@/components/Forms/utils/resolveCustomFieldValidations";
import { SignatureField, SignatureFieldRef } from "@/Features/Signatures";
import {
  StorageDefaultInfo,
  StorageFormData,
  EditStorageFormData,
} from "../storage.types";
import {
  diffFormData,
  modelDiffData,
  transformCustomAttributes,
} from "../storage.utils";
import { storageQueryKeys } from "../storage.query";

interface DefaultStorageFormProps {
  DefaultFormData?: StorageFormData; // existing values (edit/copy)
  buttonProps?: { cancelText?: string; submitText?: string };
  onSubmit: (
    data: StorageFormData,
    diffData?: EditStorageFormData,
    // undefined when the assign section (and its SignatureField) never rendered
    signatures?: { assignedBySig: SignatureFieldRef | undefined }
  ) => void | Promise<unknown>;
  onCancel: () => void;
  caseId?: number; // if provided, enables Assign section by default
  includeAssignDetails?: boolean; // show initial CoC assign section
  includeCustomFields?: boolean;
  requireStorageNumber?: boolean; // edit: blank would overwrite the identifier
  defaultInfo?: StorageDefaultInfo; // default values for create/copy
}

// Basic validation schema (aligns with current storage create/edit modals)
const storageValidationSchema = z.object({
  case_id: z.number().nullable().optional(),
  storage_number: z.string().nullable().optional(),
  type: z.string().nullable().optional(),
  serial_number: z.string().nullable().optional(),
  capacity: z.coerce.number().nullable().optional(),
  capacity_unit: z.enum(["KB", "MB", "GB", "TB", "PB"]).nullable().optional(),
  model_name: z.string().nullable().optional(),
  model_number: z.string().nullable().optional(),
  make: z.string().nullable().optional(),
  encryption_key: z.string().nullable().optional(),
  is_general_storage: z.number().nullable().optional(),
  notes: z.string().nullable().optional(),
  // Assign fields
  location_id: z.union([z.number(), z.string()]).nullable().optional(),
  timestamp: z.string().nullable().optional(),
  reason: z.string().nullable().optional(),
});

export const DefaultStorageForm: FC<DefaultStorageFormProps> = ({
  DefaultFormData,
  buttonProps,
  onSubmit,
  onCancel,
  caseId,
  includeAssignDetails,
  includeCustomFields = true,
  requireStorageNumber = false,
  defaultInfo,
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [submitting, setSubmitting] = useState(false);
  const assignedBySig = useRef<SignatureFieldRef | undefined>(undefined);

  const showAssignSection = includeAssignDetails ?? !!caseId;

  // Default general storage to Yes unless creating within a case. When a case
  // is in context the item is always case-assigned (and the toggle is hidden),
  // so caseId forces 0 ahead of any copied/default flag — a general-storage
  // source must not produce a case-assigned item flagged is_general_storage=1.
  const defaultIsGeneralStorage = caseId
    ? 0
    : (DefaultFormData?.is_general_storage as number | null | undefined) ??
      (defaultInfo?.is_general_storage as number | null | undefined) ??
      1;

  const form = useForm<StorageFormData>({
    mode: "controlled",
    initialValues: {
      capacity_unit: "GB",
      case_id: caseId || null,
      ...(DefaultFormData ? { ...DefaultFormData } : {}),
      // Only include custom attributes if custom fields are enabled
      ...(includeCustomFields
        ? transformCustomAttributes(DefaultFormData || defaultInfo)
        : {}),
      ...(DefaultFormData ? {} : defaultInfo),
      is_general_storage: defaultIsGeneralStorage,
    } as StorageFormData,
  });

  const results = useQueries({
    queries: [
      {
        queryKey: [...storageQueryKeys.types],
        queryFn: () => StorageAPI.getTypes(),
        enabled: true,
      },
      {
        queryKey: [...storageQueryKeys.providers],
        queryFn: () => StorageAPI.getProviders(),
        enabled: true,
      },
      {
        queryKey: [...storageQueryKeys.models],
        queryFn: () => StorageAPI.getStorageModels(),
        enabled: true,
      },
      {
        queryKey: [...storageQueryKeys.modelNumbers],
        queryFn: () => StorageAPI.getStorageModelNumbers(),
        enabled: true,
      },
      {
        // Shares the locations cache with DefaultEvidenceForm
        queryKey: ["evidence:locations", { include_groups: false }],
        queryFn: () => LocationsAPI.getLocations({}, { include_groups: false }),
        enabled: showAssignSection,
      },
    ],
  });

  const { data: customFieldsResponse, isFetched: customFieldsFetched } =
    CustomFieldsQuery.useGetCustomFields(
      { category: CUSTOM_FIELD_CATEGORIES.STORAGE },
      { enabled: includeCustomFields }
    );

  const isDone =
    results.every((result, index) => {
      if (index === 4 && !showAssignSection) return true; // locations
      return result.isFetched;
    }) &&
    (!includeCustomFields || customFieldsFetched);

  const fieldData = {
    types: results[0]?.data || [],
    providers: results[1]?.data || [],
    model_names: results[2]?.data || [],
    model_numbers: results[3]?.data || [],
    locations: results[4]?.data || [],
    customFields:
      customFieldsResponse?.data?.filter(
        (field: CustomField) => field.enabled === 1
      ) || [],
  };

  const capacityUnits = [
    { value: "KB", label: "KB" },
    { value: "MB", label: "MB" },
    { value: "GB", label: "GB" },
    { value: "TB", label: "TB" },
    { value: "PB", label: "PB" },
  ];

  const validateAssignFields = (formData: StorageFormData): boolean => {
    if (!showAssignSection) return true;

    const hasAssignValues = [
      formData.location_id,
      formData.timestamp,
      formData.reason,
    ].some((v) => v !== undefined && v !== null && v !== "");

    const hasAssignedBySignature =
      assignedBySig.current && !assignedBySig.current?.isEmpty?.();

    if (hasAssignValues || hasAssignedBySignature) {
      if (!formData.location_id) {
        form.setFieldError(
          "location_id",
          hasAssignedBySignature
            ? "Required when signature is provided"
            : "Required when assign details are provided"
        );
        return false;
      }
      if (!formData.timestamp) {
        form.setFieldError(
          "timestamp",
          hasAssignedBySignature
            ? "Required when signature is provided"
            : "Required when assign details are provided"
        );
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    const formData: StorageFormData = { ...form.getValues() };

    // TextInput type="number" holds a string and "" when cleared — the API
    // binds capacity raw into a numeric column, so send a number or null.
    const rawCapacity = formData.capacity as unknown;
    if (typeof rawCapacity === "string") {
      formData.capacity =
        rawCapacity.trim() === "" ? null : Number(rawCapacity);
    }

    const validationSchema = z.object({
      ...storageValidationSchema.shape,
      ...(requireStorageNumber
        ? {
            storage_number: z
              .string({ required_error: "Storage Number is required" })
              .min(1, "Storage Number is required"),
          }
        : {}),
      ...(includeCustomFields
        ? resolveCustomFieldValidations(fieldData.customFields)
        : {}),
    });

    const result = validationSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.issues.forEach((issue) => {
        if (issue.path && issue.path.length > 0) {
          fieldErrors[issue.path[0] as string] = issue.message || "Invalid";
        }
      });
      form.setErrors(fieldErrors);

      const hasCustomFieldErrors = Object.keys(fieldErrors).some((key) =>
        key.startsWith("custom_attribute_")
      );
      const hasMainFieldErrors = Object.keys(fieldErrors).some(
        (key) => !key.startsWith("custom_attribute_")
      );
      if (hasMainFieldErrors && hasCustomFieldErrors) {
        enqueueSnackbar(
          "Please complete all required fields: Main Fields and Custom Fields.",
          { variant: "error" as VariantType }
        );
      } else if (hasCustomFieldErrors) {
        enqueueSnackbar("Please complete all required fields: Custom Fields.", {
          variant: "error" as VariantType,
        });
      } else {
        enqueueSnackbar("Please complete all required fields.", {
          variant: "error" as VariantType,
        });
      }
      return;
    }

    if (!validateAssignFields(formData)) {
      // The assign section is collapsed by default, so a field error alone can
      // be invisible — surface it.
      enqueueSnackbar(
        'Please complete the required fields in "Initial Assign Details".',
        { variant: "error" as VariantType }
      );
      return;
    }

    // Diff against the original including its flattened custom_attribute_<id>
    // keys — otherwise unchanged custom-field values always register as a diff
    // (initialValues carry the flattened keys, DefaultFormData only the array).
    const originalFormData = DefaultFormData
      ? {
          ...DefaultFormData,
          ...(includeCustomFields
            ? transformCustomAttributes(DefaultFormData)
            : {}),
          // Mirror initialValues' coalescing — otherwise an item stored with
          // NULL registers a diff (and gets reclassified) even when the
          // switch was never touched.
          is_general_storage: defaultIsGeneralStorage,
        }
      : undefined;
    const diffData = diffFormData(originalFormData, formData);

    // Add cleared custom fields to diff
    if (includeCustomFields && (DefaultFormData as any)?.custom_attributes) {
      const originalCustomFields = transformCustomAttributes(
        DefaultFormData as StorageDefaultInfo
      );
      Object.keys(originalCustomFields).forEach((key) => {
        if (key.startsWith("custom_attribute_")) {
          const originalValue = (originalCustomFields as any)[key];
          const currentValue = (formData as any)[key];
          if (
            (currentValue === undefined ||
              currentValue === null ||
              currentValue === "") &&
            originalValue
          ) {
            (diffData as any)[key] = currentValue;
          }
        }
      });
    }

    const modeledDiffData = modelDiffData(diffData);

    setSubmitting(true);
    try {
      await onSubmit?.(formData, modeledDiffData, {
        assignedBySig: assignedBySig.current,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.reset();
    onCancel?.();
  };

  if (!isDone) {
    return <Loader message="Loading..." />;
  }

  return (
    <>
      <div>
        <Grid col={2}>
          <TextInput
            size="sm"
            variant="outlined"
            label="Storage Number"
            placeholder="Leave blank to auto-generate"
            description="Enter a unique ID that identifies this storage item"
            key={form.key("storage_number")}
            {...form.getInputProps("storage_number")}
          />

          <SelectBox
            size="sm"
            variant="outlined"
            label="Type"
            placeholder="Select or enter storage type..."
            description="Category of storage media (e.g. HDD, SSD, NAS)"
            searchable
            allowCustomValue
            data={(fieldData.types || []).map((t: { type: string }) => ({
              value: t.type,
              label: t.type,
            }))}
            key={form.key("type")}
            {...form.getInputProps("type")}
          />

          <TextInput
            size="sm"
            variant="outlined"
            label="Serial Number"
            placeholder="Enter a serial number for this item"
            description="Manufacturer serial number for tracking"
            key={form.key("serial_number")}
            {...form.getInputProps("serial_number")}
          />

          <Grid col={2}>
            <TextInput
              size="sm"
              variant="outlined"
              label="Size"
              placeholder="0"
              description="Total capacity of the storage media"
              type="number"
              min={0}
              key={form.key("capacity")}
              {...form.getInputProps("capacity")}
            />
            <SelectBox
              size="sm"
              variant="outlined"
              label="Size Unit"
              placeholder="Select unit"
              description="Unit of measurement for storage size"
              data={capacityUnits}
              key={form.key("capacity_unit")}
              {...form.getInputProps("capacity_unit")}
            />
          </Grid>
          <SelectBox
            size="sm"
            variant="outlined"
            label="Model Name"
            placeholder="Select or enter model name..."
            description="Product or model name of the storage device"
            searchable
            allowCustomValue
            data={(fieldData.model_names || []).map(
              (m: { model_name: string }) => ({
                value: m.model_name,
                label: m.model_name,
              })
            )}
            key={form.key("model_name")}
            {...form.getInputProps("model_name")}
          />

          <SelectBox
            size="sm"
            variant="outlined"
            label="Model Number"
            placeholder="Select or enter model number..."
            description="Manufacturer's model or part number"
            searchable
            allowCustomValue
            data={(fieldData.model_numbers || []).map(
              (m: { model_number: string }) => ({
                value: m.model_number,
                label: m.model_number,
              })
            )}
            key={form.key("model_number")}
            {...form.getInputProps("model_number")}
          />

          <SelectBox
            size="sm"
            variant="outlined"
            label="Brand"
            placeholder="Select or enter storage manufacturer..."
            description="Manufacturer or brand of the storage device"
            searchable
            allowCustomValue
            data={(fieldData.providers || []).map((p: { make: string }) => ({
              value: p.make,
              label: p.make,
            }))}
            key={form.key("make")}
            {...form.getInputProps("make")}
          />

          <TextInput
            size="sm"
            variant="outlined"
            label="Password/Encryption Key"
            placeholder="Enter encryption key"
            description="Password or key needed to access encrypted storage"
            masked={true}
            key={form.key("encryption_key")}
            {...form.getInputProps("encryption_key")}
          />

          {!caseId && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <Switch
                size="xs"
                label="General Storage"
                labelPosition="left"
                value={!!form.getValues().is_general_storage}
                onChange={(value) =>
                  form.setFieldValue("is_general_storage", value ? 1 : 0)
                }
                description={
                  <div style={{ maxWidth: 300 }}>
                    <div>
                      "General" storage items represent large, fixed storage
                      arrays (e.g. NAS) used as a permanent cache for case data
                      across multiple cases. They cannot be assigned to a case
                      and do not track chain of custody.
                    </div>{" "}
                    <div>
                      "Assignable" storage items can be assigned to cases and
                      track chain of custody. Use this for storage media that
                      must be reserved for a case to prevent usage in other
                      cases.
                    </div>
                  </div>
                }
              />
            </div>
          )}

          <div style={{ gridColumn: "1 / -1" }}>
            <TextAreaInput
              size="sm"
              variant="outlined"
              label="Description"
              placeholder="Enter a brief description of this item and any important details"
              description="Brief notes about this storage item and its purpose"
              minRows={4}
              key={form.key("notes")}
              {...form.getInputProps("notes")}
            />
          </div>
        </Grid>

        {includeCustomFields && (
          <FormSection title="Custom Fields" style={{ marginTop: 15 }}>
            {fieldData.customFields.length === 0 ? (
              <div style={{ fontSize: 12, color: "inherit", opacity: 0.8 }}>
                No{" "}
                <Link
                  to="/settings/custom-fields"
                  style={{
                    color: theme.palette.primary.main,
                    textDecoration: "underline",
                    opacity: 1,
                  }}
                >
                  storage custom fields
                </Link>{" "}
                enabled
              </div>
            ) : (
              <Grid col={2}>
                {fieldData.customFields.map((field: CustomField) => {
                  const fieldKey = `custom_attribute_${field.field_id}`;
                  return (
                    <CustomFieldRender
                      key={form.key(fieldKey)}
                      field={field}
                      inputProps={form.getInputProps(fieldKey)}
                      onChange={() => {}}
                    />
                  );
                })}
              </Grid>
            )}
          </FormSection>
        )}

        {showAssignSection && (
          <FormSection
            title={"Initial Assign Details"}
            defaultOpen={false}
            style={{ marginTop: 15 }}
          >
            <Grid col={2}>
              <SelectBox
                size="sm"
                variant="outlined"
                label="Initial Location"
                placeholder="Select location"
                description="Where the storage item will be physically stored"
                searchable
                grouped
                data={(fieldData.locations || []).map((loc: any) => ({
                  value: loc.location_id,
                  label: loc.location_name,
                  group: loc.office_name,
                }))}
                key={form.key("location_id")}
                {...form.getInputProps("location_id")}
              />

              <DateInput
                size="sm"
                variant="outlined"
                label="Timestamp"
                description="Date and time of initial assignment"
                enableCalendar
                arrow
                format="YYYY-MM-DD HH:mm:ss"
                key={form.key("timestamp")}
                {...form.getInputProps("timestamp")}
              />

              <div>
                <label
                  style={{
                    fontSize: "14px",
                    fontWeight: 500,
                    marginBottom: "8px",
                    display: "block",
                  }}
                >
                  Assigned By Signature
                </label>
                <SignatureField
                  sigFieldRef={assignedBySig}
                  label="Assigned By Signature"
                  useESignature={true}
                />
              </div>

              <div style={{ gridColumn: "1 / -1" }}>
                <TextAreaInput
                  size="sm"
                  variant="outlined"
                  label="Assign Notes"
                  description="Reason or notes for this assignment"
                  minRows={5}
                  key={form.key("reason")}
                  {...form.getInputProps("reason")}
                />
              </div>
            </Grid>
          </FormSection>
        )}

        <ButtonMenu>
          <Button size="xs" variant="subtle" onClick={handleCancel}>
            {buttonProps?.cancelText || "Cancel"}
          </Button>
          <Button
            size="xs"
            color="primary"
            variant="contained"
            loading={submitting}
            disabled={submitting}
            onClick={handleSubmit}
          >
            {buttonProps?.submitText || "Submit"}
          </Button>
        </ButtonMenu>
      </div>
    </>
  );
};
